import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge, Row, Col, ButtonGroup } from 'react-bootstrap';
import { 
  FaCheck, 
  FaTimes, 
  FaClipboardCheck, 
  FaUser, 
  FaPaperPlane, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaUserTie,
  FaCalendarAlt,
  FaClipboardList,
  FaCommentAlt,
  FaEdit,
  FaSave
} from 'react-icons/fa';
import '../CSS/ChecklistModal.css';

const ChecklistModal = ({ show, onHide, userId, memberIndex = -1, formType, mode = 'preview' }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [editableItems, setEditableItems] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (show && userId) {
      fetchChecklist();
    }
  }, [show, userId, memberIndex, formType, mode]);
  
  // Safety check to prevent null userId errors
  if (show && !userId) {
    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Checklist Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">Cannot load checklist: Missing user ID</Alert>
        </Modal.Body>
      </Modal>
    );
  }

  const fetchChecklist = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      
      // Get existing checklist - use the mode to determine the type (prepare or review)
      const checklistType = mode === 'review' ? 'review' : 'prepare';
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists?userId=${userId}&memberIndex=${memberIndex}&formType=${formType}&type=${checklistType}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No ${checklistType} checklist found. ${checklistType === 'review' ? 'Please ensure a prepare checklist has been submitted first.' : 'The employee has not prepared a checklist yet.'}`);
        }
        throw new Error(`Failed to fetch ${checklistType} checklist`);
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      
      // Initialize editable items if in review mode
      if (mode === 'review') {
        setEditableItems(data.checklist.items.map(item => ({
          response: item.response || '',
          remarks: item.remarks || ''
        })));
      }
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(err.message || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteChecklist = async () => {
    if (!checklist) return;
    
    // If in review mode and editing, save changes first
    if (mode === 'review' && isEditing) {
      await handleSaveChanges();
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = JSON.parse(localStorage.getItem('adminInfo'));
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          adminId: adminData._id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      setSuccess('Checklist review completed successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error completing checklist:', err);
      setError(err.message || 'Failed to complete checklist');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSaveChanges = async () => {
    if (!checklist) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = JSON.parse(localStorage.getItem('adminInfo'));
      
      // Save each item that has been modified
      const savePromises = checklist.items.map(async (item, index) => {
        if (editableItems[index].response !== item.response || editableItems[index].remarks !== item.remarks) {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/update-item`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              checklistId: checklist._id,
              itemIndex: index,
              response: editableItems[index].response,
              remarks: editableItems[index].remarks
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to update item ${index + 1}`);
          }
          
          return await response.json();
        }
        return null;
      });
      
      await Promise.all(savePromises.filter(p => p !== null));
      
      // Refresh the checklist data
      await fetchChecklist();
      
      setIsEditing(false);
      setSuccess('Changes saved successfully!');
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error saving changes:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmitReviewChecklist = async () => {
    if (!checklist) return;
    
    // If editing, save changes first
    if (isEditing) {
      await handleSaveChanges();
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = JSON.parse(localStorage.getItem('adminInfo'));
      
      // Check if all items have responses
      const hasIncompleteItems = editableItems.some(item => !item.response);
      
      if (hasIncompleteItems) {
        throw new Error('All checklist items must have a response (Yes/No) before submission');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          adminId: adminData._id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit review checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      setSuccess('Review checklist submitted successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error submitting review checklist:', err);
      setError(err.message || 'Failed to submit review checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const getFormTitle = () => {
    switch (formType) {
      case '1040':
        return '1040 - Individual Tax Return';
      case '1120S':
        return '1120S - S Corporation Tax Return';
      case '1120':
        return '1120 - C Corporation Tax Return';
      case '1065':
        return '1065 - Partnership Tax Return';
      default:
        return formType;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge bg="secondary">Draft</Badge>;
      case 'submitted':
        return <Badge bg="warning">Submitted</Badge>;
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      default:
        return <Badge bg="light">Unknown</Badge>;
    }
  };

  const renderMetadataItem = (icon, label, value) => (
    <div className="d-flex align-items-center mb-2">
      <span className="me-2 text-muted">{icon}</span>
      <span className="small"><strong>{label}:</strong> {value}</span>
    </div>
  );

  const getResponseIcon = (response) => {
    switch (response) {
      case 'Yes': return <FaCheck className="text-success" />;
      case 'No': return <FaTimes className="text-danger" />;
      case 'NA': return <span className="text-warning">N/A</span>;
      default: return <span className="text-muted">-</span>;
    }
  };
  
  const handleResponseChange = (index, value) => {
    const updatedItems = [...editableItems];
    updatedItems[index].response = value;
    setEditableItems(updatedItems);
  };
  
  const handleRemarksChange = (index, value) => {
    const updatedItems = [...editableItems];
    updatedItems[index].remarks = value;
    setEditableItems(updatedItems);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="checklist-modal">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="w-100">
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center">
              <FaClipboardCheck className="me-2" />
              {getFormTitle()} Checklist
              {checklist && <span className="ms-2">{getStatusBadge(checklist.status)}</span>}
            </div>
            <div className="mt-1">
              <Badge bg="light" text="dark" className="text-uppercase">
                <FaUser className="me-1" />
                {memberIndex === -1 ? 'Primary Client' : `Member ${memberIndex + 1}`}
              </Badge>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="light" className="text-primary" style={{ width: '3rem', height: '3rem' }} />
            <h5 className="mt-3">Loading Checklist</h5>
            <p className="text-muted">Please wait while we prepare the checklist...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="d-flex align-items-center">
            <FaExclamationTriangle className="flex-shrink-0 me-3" size={24} />
            <div>
              <h5 className="alert-heading">Error Loading Checklist</h5>
              <p className="mb-0">{error}</p>
            </div>
          </Alert>
        ) : success ? (
          <Alert variant="success" className="d-flex align-items-center">
            <FaCheckCircle className="flex-shrink-0 me-3" size={24} />
            <div>
              <h5 className="alert-heading">Success!</h5>
              <p className="mb-0">{success}</p>
            </div>
          </Alert>
        ) : checklist ? (
          <>
            <div className="checklist-metadata bg-light p-3 rounded-3 mb-4">
              <h6 className="d-flex align-items-center mb-3">
                <FaClipboardList className="me-2" />
                <span>Checklist Information</span>
              </h6>
              <div className="row">
                <div className="col-md-6">
                  {renderMetadataItem(
                    <FaUserTie />,
                    'Status',
                    <Badge bg={checklist.status === 'submitted' ? 'warning' : 'success'} className="text-uppercase">
                      {checklist.status}
                    </Badge>
                  )}
                  {checklist.submittedBy && renderMetadataItem(
                    <FaUser />,
                    'Prepared by',
                    `Employee (${checklist.submittedBy.name || 'N/A'})`
                  )}
                </div>
                <div className="col-md-6">
                  {checklist.submittedAt && renderMetadataItem(
                    <FaCalendarAlt />,
                    'Submitted on',
                    new Date(checklist.submittedAt).toLocaleString()
                  )}
                  {checklist.completedAt && renderMetadataItem(
                    <FaCalendarAlt />,
                    'Reviewed on',
                    new Date(checklist.completedAt).toLocaleString()
                  )}
                </div>
              </div>
            </div>
            
            <div className="checklist-items">
              {checklist.items.map((item, index) => (
                <div 
                  key={index} 
                  className={`checklist-item p-3 mb-3 rounded-3 ${
                    (isEditing ? editableItems[index].response : item.response) === 'Yes' ? 'border-start border-success' : 
                    (isEditing ? editableItems[index].response : item.response) === 'No' ? 'border-start border-danger' : 
                    'border-start border-secondary'
                  }`}
                  style={{ backgroundColor: 'var(--bs-light-bg-subtle)' }}
                >
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-3 d-flex align-items-center">
                        <span className="badge bg-secondary me-2">{index + 1}</span>
                        {item.text}
                      </h6>
                      
                      {mode === 'review' && isEditing ? (
                        <>
                          <div className="mb-3">
                            <Form.Label className="text-muted mb-2">Response:</Form.Label>
                            <ButtonGroup className="w-100">
                              <Button 
                                variant={editableItems[index].response === 'Yes' ? 'success' : 'outline-success'}
                                onClick={() => handleResponseChange(index, 'Yes')}
                                className="w-50"
                              >
                                <FaCheck className="me-1" /> Yes
                              </Button>
                              <Button 
                                variant={editableItems[index].response === 'No' ? 'danger' : 'outline-danger'}
                                onClick={() => handleResponseChange(index, 'No')}
                                className="w-50"
                              >
                                <FaTimes className="me-1" /> No
                              </Button>
                            </ButtonGroup>
                          </div>
                          
                          <div className="mb-2">
                            <Form.Label className="text-muted mb-2">Remarks:</Form.Label>
                            <Form.Control 
                              as="textarea" 
                              rows={2}
                              value={editableItems[index].remarks || ''}
                              onChange={(e) => handleRemarksChange(index, e.target.value)}
                              placeholder="Add any comments or notes here..."
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-muted">Response:</span>
                            <div className="d-flex align-items-center">
                              {getResponseIcon(isEditing ? editableItems[index].response : item.response)}
                              <span className="ms-1 small">{(isEditing ? editableItems[index].response : item.response) || 'Not answered'}</span>
                            </div>
                          </div>

                          {(isEditing ? editableItems[index].remarks : item.remarks) && (
                            <div className="mt-2">
                              <div className="small text-muted mb-1 d-flex align-items-center">
                                <FaCommentAlt className="me-1" />
                                <span>Remarks</span>
                              </div>
                              <div className="p-2 bg-white rounded border">
                                {isEditing ? editableItems[index].remarks : item.remarks}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {mode === 'review' && (
              <div className="d-flex justify-content-between mt-4">
                <Button 
                  variant="outline-secondary" 
                  onClick={onHide}
                >
                  <FaTimes className="me-1" /> Close
                </Button>
                
                <div>
                  {checklist.status === 'draft' && (
                    <>
                      {isEditing ? (
                        <Button
                          variant="primary"
                          onClick={handleSaveChanges}
                          disabled={submitting}
                          className="me-2"
                        >
                          {submitting ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <FaSave className="me-1" /> Save Changes
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="outline-primary"
                          onClick={() => setIsEditing(true)}
                          className="me-2"
                        >
                          <FaEdit className="me-1" /> Edit Responses
                        </Button>
                      )}
                      
                      <Button
                        variant="success"
                        onClick={handleSubmitReviewChecklist}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <FaPaperPlane className="me-1" /> Submit Review
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  
                  {checklist.status === 'submitted' && (
                    <Button
                      variant="success"
                      onClick={handleCompleteChecklist}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Completing...
                        </>
                      ) : (
                        <>
                          <FaCheck className="me-1" /> Complete Review
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <Alert variant="warning" className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2" />
            <span>No checklist data available for this client/member.</span>
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChecklistModal;
